import { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatIDR, formatDateID, formatNumber } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Plus, Pencil, Trash2, Search, Package, Percent, XCircle, Upload, Download, ZoomIn, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import CurrencyInput from "@/components/CurrencyInput";
import { requireStockUnlock } from "@/lib/stockLock";
import { Checkbox } from "@/components/ui/checkbox";

const computeMarkup = (buy, sell) => {
  const b = Number(buy) || 0;
  const s = Number(sell) || 0;
  if (b <= 0) return 0;
  return ((s - b) / b) * 100;
};

const formatPct = (n) => {
  const v = Number(n) || 0;
  return (v >= 0 ? "+" : "") + Math.round(v) + "%";
};

const emptyForm = {
  code: "",
  name: "",
  model: "",
  category: "",
  unit: "pcs",
  brand: "",
  brand_category: "",
  vendor: "",
  po_number: "",
  invoice_number: "",
  description: "",
  purchase_date: "",
  stock_gudang: 0,
  stock_toko: 0,
  purchase_price: 0,
  sell_price: 0,
};

export default function Products() {
  const { user, hasMenuAccess } = useAuth();
  const isOwner = user?.role === "owner";
  const canCreateInvoice = hasMenuAccess("invoices");
  const canBulkSelect = isOwner || canCreateInvoice;
  const canViewPurchasePrice = hasMenuAccess("products_view_purchase_price");
  const navigate = useNavigate();
  const canEditCategory = hasMenuAccess("products_edit_category");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [brandCategoryFilter, setBrandCategoryFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [brandFilter, setBrandFilter] = useState("all");
  const [brandOpen, setBrandOpen] = useState(false);
  // Default sort: Kategori A→Z. Persisted so refresh keeps the choice.
  const PRODUCTS_SORT_KEY = "products_sort_by_v2";
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem(PRODUCTS_SORT_KEY) || "category-asc"; }
    catch { return "category-asc"; }
  });
  useEffect(() => {
    try { localStorage.setItem(PRODUCTS_SORT_KEY, sortBy); } catch { /* ignore */ }
  }, [sortBy]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    category: "",
    brand: "",
    markup_percent: "",
    adjust_gudang: "",
    adjust_toko: "",
  });
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(await requireStockUnlock())) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/products/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created, updated, errors } = r.data;
      toast.success(`${created} baru · ${updated} diperbarui`);
      if (errors && errors.length) {
        toast.error(`${errors.length} baris bermasalah: ${errors[0]}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import gagal");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const load = async () => {
    const r = await api.get("/products");
    setItems(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = items.filter((p) => {
      if (brandCategoryFilter && (p.brand_category || "") !== brandCategoryFilter) return false;
      if (categoryFilter !== "all" && (p.category || "").trim() !== categoryFilter) return false;
      if (brandFilter !== "all" && (p.brand || "").trim() !== brandFilter) return false;
      if (!s) return true;
      return (
        p.name.toLowerCase().includes(s) ||
        p.code.toLowerCase().includes(s) ||
        (p.category || "").toLowerCase().includes(s) ||
        (p.brand || "").toLowerCase().includes(s) ||
        (p.brand_category || "").toLowerCase().includes(s)
      );
    });
    const sorted = [...list];
    switch (sortBy) {
      case "name-asc":
        sorted.sort((a, b) => (a.name || "").localeCompare(b.name || "", "id", { numeric: true }));
        break;
      case "name-desc":
        sorted.sort((a, b) => (b.name || "").localeCompare(a.name || "", "id", { numeric: true }));
        break;
      case "code-asc":
        sorted.sort((a, b) => (a.code || "").localeCompare(b.code || "", "id", { numeric: true }));
        break;
      case "model-asc":
        sorted.sort((a, b) => (a.model || "").localeCompare(b.model || "", "id", { numeric: true }));
        break;
      case "model-desc":
        sorted.sort((a, b) => (b.model || "").localeCompare(a.model || "", "id", { numeric: true }));
        break;
      case "stock-desc":
        sorted.sort((a, b) => ((b.stock_gudang || 0) + (b.stock_toko || 0)) - ((a.stock_gudang || 0) + (a.stock_toko || 0)));
        break;
      case "stock-asc":
        sorted.sort((a, b) => ((a.stock_gudang || 0) + (a.stock_toko || 0)) - ((b.stock_gudang || 0) + (b.stock_toko || 0)));
        break;
      case "price-desc":
        sorted.sort((a, b) => (Number(b.sell_price) || 0) - (Number(a.sell_price) || 0));
        break;
      case "price-asc":
        sorted.sort((a, b) => (Number(a.sell_price) || 0) - (Number(b.sell_price) || 0));
        break;
      case "category-asc":
        sorted.sort((a, b) =>
          (a.category || "").localeCompare(b.category || "", "id", { numeric: true }) ||
          (a.name || "").localeCompare(b.name || "", "id", { numeric: true }),
        );
        break;
      case "category-desc":
        sorted.sort((a, b) =>
          (b.category || "").localeCompare(a.category || "", "id", { numeric: true }) ||
          (a.name || "").localeCompare(b.name || "", "id", { numeric: true }),
        );
        break;
      default:
        break;
    }
    return sorted;
  }, [items, q, brandCategoryFilter, categoryFilter, brandFilter, sortBy]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    items.forEach((p) => {
      const c = (p.category || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [items]);

  const brandOptions = useMemo(() => {
    const set = new Set();
    items.forEach((p) => {
      const b = (p.brand || "").trim();
      if (b) set.add(b);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [items]);

  const brandCategories = useMemo(() => {
    const map = {};
    items.forEach((p) => {
      const bc = (p.brand_category || "").trim();
      if (bc) map[bc] = (map[bc] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    // Normalize purchase_date to YYYY-MM-DD for the native date input
    let pd = "";
    const raw = p.purchase_date || "";
    if (raw) {
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        pd = raw.slice(0, 10);
      } else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(raw)) {
        const [d, m, y] = raw.split("/");
        pd = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      }
    }
    setForm({
      code: p.code,
      name: p.name,
      model: p.model || "",
      category: p.category || "",
      unit: p.unit || "pcs",
      brand: p.brand || "",
      brand_category: p.brand_category || "",
      vendor: p.vendor || "",
      po_number: p.po_number || "",
      invoice_number: p.invoice_number || "",
      description: p.description || "",
      purchase_date: pd,
      stock_gudang: p.stock_gudang ?? 0,
      stock_toko: p.stock_toko ?? 0,
      purchase_price: p.purchase_price ?? 0,
      sell_price: p.sell_price ?? 0,
      min_stock: p.min_stock ?? 0,
    });
    setOpen(true);
  };

  const [descView, setDescView] = useState(null); // { name, code, description }
  const [codeEditId, setCodeEditId] = useState(null);
  const [codeEditValue, setCodeEditValue] = useState("");
  const [poEditId, setPoEditId] = useState(null);
  const [poEditValue, setPoEditValue] = useState("");
  const [mtpEditId, setMtpEditId] = useState(null);
  const [mtpEditValue, setMtpEditValue] = useState("");
  const [ltcEditId, setLtcEditId] = useState(null);
  const [ltcEditValue, setLtcEditValue] = useState("");

  const beginCodeEdit = (p) => {
    if (!isOwner) return;
    setCodeEditId(p.id);
    setCodeEditValue(p.code || "");
  };
  const cancelCodeEdit = () => {
    setCodeEditId(null);
    setCodeEditValue("");
  };
  const saveCodeEdit = async (p) => {
    const newCode = codeEditValue.trim();
    if (!newCode) {
      toast.error("Part Number wajib diisi");
      return;
    }
    if (newCode === (p.code || "").trim()) {
      cancelCodeEdit();
      return;
    }
    if (!(await requireStockUnlock())) {
      cancelCodeEdit();
      return;
    }
    try {
      await api.patch(`/products/${p.id}`, { code: newCode });
      toast.success("Part Number diperbarui");
      cancelCodeEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui Part Number");
    }
  };

  const beginPoEdit = (p) => {
    setPoEditId(p.id);
    setPoEditValue(p.po_number || "");
  };
  const cancelPoEdit = () => {
    setPoEditId(null);
    setPoEditValue("");
  };
  const savePoEdit = async (p) => {
    const newPo = poEditValue.trim();
    if (newPo === (p.po_number || "").trim()) {
      cancelPoEdit();
      return;
    }
    if (!(await requireStockUnlock())) {
      cancelPoEdit();
      return;
    }
    try {
      await api.patch(`/products/${p.id}`, { po_number: newPo });
      toast.success("No. PO diperbarui");
      cancelPoEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui No. PO");
    }
  };

  // Quick edit for Qty MTP (stock_gudang) and Qty LTC (stock_toko).
  // Uses the dedicated `/products/{id}/stock` endpoint so we go through
  // the same audit/validation path as the bulk stock adjuster.
  const beginMtpEdit = (p) => {
    if (!isOwner) return;
    setMtpEditId(p.id);
    setMtpEditValue(String(p.stock_gudang ?? 0));
  };
  const cancelMtpEdit = () => {
    setMtpEditId(null);
    setMtpEditValue("");
  };
  const saveMtpEdit = async (p) => {
    const raw = String(mtpEditValue).trim();
    if (raw === "") { cancelMtpEdit(); return; }
    const currentMtp = p.stock_gudang ?? 0;
    const currentLtc = p.stock_toko ?? 0;
    const totalPool = currentMtp + currentLtc;
    let desiredMtp = Math.max(0, Math.floor(Number(raw) || 0));
    if (desiredMtp === currentMtp) { cancelMtpEdit(); return; }
    // Transfer semantics: total stock stays constant, delta moves to/from LTC.
    let capped = false;
    if (desiredMtp > totalPool) { desiredMtp = totalPool; capped = true; }
    const newLtc = totalPool - desiredMtp;
    if (!(await requireStockUnlock())) { cancelMtpEdit(); return; }
    try {
      await api.patch(`/products/${p.id}/stock`, {
        stock_gudang: desiredMtp,
        stock_toko: newLtc,
      });
      const moved = Math.abs(desiredMtp - currentMtp);
      if (desiredMtp < currentMtp) {
        toast.success(`${moved} unit dipindahkan MTP → LTC`);
      } else {
        toast.success(`${moved} unit dipindahkan LTC → MTP${capped ? " (dibatasi stok LTC)" : ""}`);
      }
      cancelMtpEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui Qty MTP");
    }
  };

  const beginLtcEdit = (p) => {
    if (!isOwner) return;
    setLtcEditId(p.id);
    setLtcEditValue(String(p.stock_toko ?? 0));
  };
  const cancelLtcEdit = () => {
    setLtcEditId(null);
    setLtcEditValue("");
  };
  const saveLtcEdit = async (p) => {
    const raw = String(ltcEditValue).trim();
    if (raw === "") { cancelLtcEdit(); return; }
    const currentMtp = p.stock_gudang ?? 0;
    const currentLtc = p.stock_toko ?? 0;
    const totalPool = currentMtp + currentLtc;
    let desiredLtc = Math.max(0, Math.floor(Number(raw) || 0));
    if (desiredLtc === currentLtc) { cancelLtcEdit(); return; }
    // Transfer semantics: total stock stays constant, delta moves to/from MTP.
    let capped = false;
    if (desiredLtc > totalPool) { desiredLtc = totalPool; capped = true; }
    const newMtp = totalPool - desiredLtc;
    if (!(await requireStockUnlock())) { cancelLtcEdit(); return; }
    try {
      await api.patch(`/products/${p.id}/stock`, {
        stock_gudang: newMtp,
        stock_toko: desiredLtc,
      });
      const moved = Math.abs(desiredLtc - currentLtc);
      if (desiredLtc < currentLtc) {
        toast.success(`${moved} unit dipindahkan LTC → MTP`);
      } else {
        toast.success(`${moved} unit dipindahkan MTP → LTC${capped ? " (dibatasi stok MTP)" : ""}`);
      }
      cancelLtcEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui Qty LTC");
    }
  };

  const guardStockInput = async (e) => {
    const ok = await requireStockUnlock();
    if (!ok) e.target.blur();
  };

  const save = async (e) => {
    e.preventDefault();
    if (!(await requireStockUnlock({ force: true }))) return;
    const payload = {
      code: (form.code || "").trim(),
      name: (form.name || "").trim(),
      model: (form.model || "").trim(),
      category: (form.category || "").trim(),
      unit: (form.unit || "").trim() || "pcs",
      brand: (form.brand || "").trim(),
      brand_category: (form.brand_category || "").trim(),
      vendor: (form.vendor || "").trim(),
      po_number: (form.po_number || "").trim(),
      invoice_number: (form.invoice_number || "").trim(),
      description: (form.description || "").trim(),
      purchase_date: (form.purchase_date || "").trim() || null,
      purchase_year: form.purchase_date
        ? Number(String(form.purchase_date).slice(0, 4)) || null
        : null,
      stock_gudang: Number(form.stock_gudang) || 0,
      stock_toko: Number(form.stock_toko) || 0,
      purchase_price: Number(form.purchase_price) || 0,
      sell_price: Number(form.sell_price) || 0,
      min_stock: Number(form.min_stock) || 0,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Barang diperbarui");
      } else {
        await api.post("/products", payload);
        toast.success("Barang ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus barang "${p.name}"?`)) return;
    if (!(await requireStockUnlock())) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Barang dihapus");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  // ---- Bulk selection & actions ---------------------------------------
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const allSelectedInView =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedIds.has(id));

  const toggleAllInView = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedInView) {
        allFilteredIds.forEach((id) => next.delete(id));
      } else {
        allFilteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Hapus ${selectedIds.size} barang terpilih? Tindakan ini tidak bisa dibatalkan.`)) return;
    if (!(await requireStockUnlock())) return;
    try {
      await Promise.all(Array.from(selectedIds).map((id) => api.delete(`/products/${id}`)));
      toast.success(`${selectedIds.size} barang dihapus`);
      clearSelection();
      load();
    } catch (err) {
      toast.error("Sebagian gagal dihapus");
      load();
    }
  };

  const openBulkEdit = () => {
    setBulkForm({ category: "", brand: "", markup_percent: "", adjust_gudang: "", adjust_toko: "" });
    setBulkOpen(true);
  };

  // Send the currently selected products to the Invoices page as an initial
  // line-up for a new sales invoice. We stash the payload in sessionStorage
  // because react-router state gets tricky with our Layout/RoleGuard wrappers.
  const buatFakturFromSelection = () => {
    if (selectedIds.size === 0) return;
    const picks = Array.from(selectedIds)
      .map((id) => items.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => ({
        product_id: p.id,
        product_code: p.code || "",
        product_name: p.name || "",
        price: Number(p.sell_price) || 0,
      }));
    if (picks.length === 0) return;
    try {
      sessionStorage.setItem(
        "quick_invoice_prefill_v1",
        JSON.stringify({ items: picks, ts: Date.now() }),
      );
    } catch {
      /* ignore quota errors */
    }
    clearSelection();
    navigate("/faktur");
  };

  const runBulkEdit = async (e) => {
    const idList = Array.from(selectedIds);
    if (idList.length === 0) return;

    const patch = {};
    if (bulkForm.category.trim()) patch.category = bulkForm.category.trim();
    if (bulkForm.brand.trim()) patch.brand = bulkForm.brand.trim();

    const markupStr = String(bulkForm.markup_percent || "").trim();
    const markupPct = markupStr === "" ? null : Number(markupStr);
    const adjG = bulkForm.adjust_gudang === "" ? 0 : Number(bulkForm.adjust_gudang);
    const adjT = bulkForm.adjust_toko === "" ? 0 : Number(bulkForm.adjust_toko);

    const hasPatch = Object.keys(patch).length > 0;
    const hasMarkup = markupPct !== null && !Number.isNaN(markupPct);
    if (!hasPatch && !hasMarkup && !adjG && !adjT) {
      toast.error("Isi minimal satu field untuk diubah");
      return;
    }
    if (!(await requireStockUnlock())) return;

    try {
      await Promise.all(
        idList.map(async (id) => {
          const prod = items.find((p) => p.id === id);
          if (!prod) return;
          const perProduct = { ...patch };
          if (hasMarkup && prod.purchase_price > 0) {
            perProduct.sell_price = Math.round(prod.purchase_price * (1 + markupPct / 100));
          }
          if (Object.keys(perProduct).length > 0) {
            await api.patch(`/products/${id}`, perProduct);
          }
          if (adjG || adjT) {
            const stockPatch = {};
            if (adjG) stockPatch.stock_gudang = Math.max(0, (prod.stock_gudang || 0) + adjG);
            if (adjT) stockPatch.stock_toko = Math.max(0, (prod.stock_toko || 0) + adjT);
            await api.patch(`/products/${id}/stock`, stockPatch);
          }
        })
      );
      toast.success(`${idList.length} barang diperbarui`);
      setBulkOpen(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error("Sebagian gagal diperbarui");
      load();
    }
  };


  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Manajemen Barang</h1>
          <p className="text-sm text-zinc-500 mt-1">Kelola daftar barang, harga, dan stok.</p>
        </div>
        {isOwner && (
          <Button data-testid="btn-add-product" onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 rounded-md">
            <Plus className="h-4 w-4 mr-1.5" />
            Tambah Barang
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 no-print" data-testid="products-io-bar">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          data-testid="products-import-file"
          onChange={handleImportFile}
        />
        {isOwner && (
          <Button
            data-testid="btn-import-xlsx"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="rounded-md"
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {importing ? "Mengimpor…" : "Import Excel"}
          </Button>
        )}
        {isOwner && (
          <Button
            data-testid="btn-export-xlsx"
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!(await requireStockUnlock())) return;
              window.open(`${API_BASE}/products/export-xlsx`, "_blank");
            }}
            className="rounded-md"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export Excel
          </Button>
        )}
        {isOwner && (
          <span className="text-[11px] text-zinc-500 ml-1">
            Import Excel akan **update** produk yang kodenya sudah ada (file jadi data terbaru). Baris duplikat di Excel yang tersisa masuk sebagai barang baru.
          </span>
        )}
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md">
        {brandCategories.length > 0 && (
          <div className="p-4 border-b border-zinc-200 flex flex-wrap items-center gap-2" data-testid="brand-category-filter">
            <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Kategori Merek:</span>
            <button
              data-testid="brand-cat-all"
              onClick={() => setBrandCategoryFilter("")}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-150 ${
                brandCategoryFilter === ""
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
            >
              Semua <span className="ml-1 opacity-70">{items.length}</span>
            </button>
            {brandCategories.map(([bc, count]) => (
              <button
                key={bc}
                data-testid={`brand-cat-chip-${bc}`}
                onClick={() => setBrandCategoryFilter(bc)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-150 ${
                  brandCategoryFilter === bc
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {bc} <span className="ml-1 opacity-70">{count}</span>
              </button>
            ))}
          </div>
        )}
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="products-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama, part number, atau kategori…"
              className="pl-9 h-10 rounded-md"
            />
          </div>
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={categoryOpen}
                data-testid="products-filter-category"
                className="h-10 w-[220px] justify-between font-normal"
              >
                {categoryFilter === "all"
                  ? "Semua Kategori"
                  : categoryFilter}
                <svg
                  className="ml-2 h-4 w-4 shrink-0 opacity-50"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Cari kategori…"
                  data-testid="products-filter-category-search"
                  className="h-9"
                />
                <CommandList>
                  <CommandEmpty>Kategori tidak ditemukan.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="Semua Kategori"
                      onSelect={() => {
                        setCategoryFilter("all");
                        setCategoryOpen(false);
                      }}
                      data-testid="cat-option-all"
                    >
                      Semua Kategori
                    </CommandItem>
                    {categoryOptions.map((c) => (
                      <CommandItem
                        key={c}
                        value={c}
                        onSelect={() => {
                          setCategoryFilter(c);
                          setCategoryOpen(false);
                        }}
                        data-testid={`cat-option-${c}`}
                      >
                        {c}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Popover open={brandOpen} onOpenChange={setBrandOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={brandOpen}
                data-testid="products-filter-brand"
                className="h-10 w-[200px] justify-between font-normal"
              >
                {brandFilter === "all" ? "Semua Brand" : brandFilter}
                <svg
                  className="ml-2 h-4 w-4 shrink-0 opacity-50"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Cari brand…"
                  data-testid="products-filter-brand-search"
                  className="h-9"
                />
                <CommandList>
                  <CommandEmpty>Brand tidak ditemukan.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="Semua Brand"
                      onSelect={() => {
                        setBrandFilter("all");
                        setBrandOpen(false);
                      }}
                      data-testid="brand-option-all"
                    >
                      Semua Brand
                    </CommandItem>
                    {brandOptions.map((b) => (
                      <CommandItem
                        key={b}
                        value={b}
                        onSelect={() => {
                          setBrandFilter(b);
                          setBrandOpen(false);
                        }}
                        data-testid={`brand-option-${b}`}
                      >
                        {b}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10 w-[210px]" data-testid="products-sort-by">
              <SelectValue placeholder="Urutkan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">Nama Barang (A → Z)</SelectItem>
              <SelectItem value="name-desc">Nama Barang (Z → A)</SelectItem>
              <SelectItem value="category-asc">Kategori (A → Z)</SelectItem>
              <SelectItem value="category-desc">Kategori (Z → A)</SelectItem>
              <SelectItem value="code-asc">Part Number (A → Z)</SelectItem>
              <SelectItem value="model-asc">Model (A → Z)</SelectItem>
              <SelectItem value="model-desc">Model (Z → A)</SelectItem>
              <SelectItem value="stock-desc">Stok Terbanyak</SelectItem>
              <SelectItem value="stock-asc">Stok Tersedikit</SelectItem>
              <SelectItem value="price-desc">Harga Jual Tertinggi</SelectItem>
              <SelectItem value="price-asc">Harga Jual Terendah</SelectItem>
              <SelectItem value="default">Urutan Default (ID)</SelectItem>
            </SelectContent>
          </Select>
          {(categoryFilter !== "all" || brandFilter !== "all" || sortBy !== "category-asc" || q) && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setQ("");
                setCategoryFilter("all");
                setBrandFilter("all");
                setSortBy("category-asc");
              }}
              className="h-9 text-xs"
              data-testid="products-filter-reset"
            >
              Reset
            </Button>
          )}
          <div className="text-xs text-zinc-500">
            {filtered.length} dari {items.length} barang
          </div>
        </div>

        {canBulkSelect && selectedIds.size > 0 && (
          <div
            className="px-4 py-3 border-b border-zinc-200 bg-zinc-900 text-white flex items-center gap-3 flex-wrap"
            data-testid="bulk-actions-bar"
          >
            <div className="text-sm font-medium">
              {selectedIds.size} barang dipilih
            </div>
            <div className="flex-1" />
            {canCreateInvoice && (
              <Button
                data-testid="btn-bulk-create-invoice"
                size="sm"
                variant="outline"
                onClick={buatFakturFromSelection}
                className="h-8 rounded-md bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600 hover:text-white"
              >
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                Buat Faktur Penjualan
              </Button>
            )}
            {isOwner && (
              <>
                <Button
                  data-testid="btn-bulk-edit"
                  size="sm"
                  variant="outline"
                  onClick={openBulkEdit}
                  className="h-8 rounded-md bg-white text-zinc-900 hover:bg-zinc-100"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit Massal
                </Button>
                <Button
                  data-testid="btn-bulk-delete"
                  size="sm"
                  variant="outline"
                  onClick={bulkDelete}
                  className="h-8 rounded-md bg-rose-600 text-white border-rose-600 hover:bg-rose-700 hover:text-white"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Hapus Terpilih
                </Button>
              </>
            )}
            <Button
              data-testid="btn-bulk-clear"
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="h-8 rounded-md text-white hover:bg-white/10 hover:text-white"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              Batal
            </Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                {canBulkSelect && (
                  <TableHead className="w-[40px]">
                    <Checkbox
                      data-testid="bulk-select-all"
                      checked={allSelectedInView}
                      onCheckedChange={toggleAllInView}
                      aria-label="Pilih semua"
                    />
                  </TableHead>
                )}
                <TableHead className="w-[50px] text-right text-xs text-zinc-500">No.</TableHead>
                <TableHead className="w-[120px]">
                  <button
                    type="button"
                    onClick={() =>
                      setSortBy((s) => (s === "code-asc" ? "default" : "code-asc"))
                    }
                    data-testid="products-th-sort-code"
                    title="Urutkan Part Number secara natural (1,2,3,…,10)"
                    className={`inline-flex items-center gap-1 hover:text-zinc-900 ${sortBy === "code-asc" ? "text-zinc-900 font-semibold" : ""}`}
                  >
                    Part Number
                    <span className="text-[10px] opacity-60">
                      {sortBy === "code-asc" ? "▲" : "↕"}
                    </span>
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() =>
                      setSortBy((s) =>
                        s === "name-asc" ? "name-desc" : s === "name-desc" ? "default" : "name-asc",
                      )
                    }
                    data-testid="products-th-sort-name"
                    title="Urutkan Nama Barang secara natural (1,2,3,…,10)"
                    className={`inline-flex items-center gap-1 hover:text-zinc-900 ${sortBy === "name-asc" || sortBy === "name-desc" ? "text-zinc-900 font-semibold" : ""}`}
                  >
                    Nama Barang
                    <span className="text-[10px] opacity-60">
                      {sortBy === "name-asc" ? "▲" : sortBy === "name-desc" ? "▼" : "↕"}
                    </span>
                  </button>
                </TableHead>
                <TableHead>Brand</TableHead>
                <TableHead className="hidden md:table-cell">Kategori</TableHead>
                <TableHead className="text-right">Qty MTP</TableHead>
                <TableHead className="text-right">Qty LTC</TableHead>
                <TableHead className="text-right">Total Stok</TableHead>
                {canViewPurchasePrice && (
                  <TableHead className="text-right hidden md:table-cell">Hrg Beli</TableHead>
                )}
                <TableHead className="text-right">Hrg Jual</TableHead>
                <TableHead className="text-right">PPN 11%</TableHead>
                {isOwner && <TableHead className="w-[100px] text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody data-testid="products-table-body">
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-10 text-zinc-500">
                    <Package className="h-6 w-6 mx-auto mb-2 opacity-40" />
                    Belum ada barang.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((p, idx) => {
                const total = (p.stock_gudang ?? 0) + (p.stock_toko ?? 0);
                const isSelected = selectedIds.has(p.id);
                return (
                  <TableRow
                    key={p.id}
                    data-state={isSelected ? "selected" : undefined}
                    className={`transition-colors duration-150 ${
                      isSelected ? "bg-zinc-100 hover:bg-zinc-100" : "hover:bg-zinc-50"
                    }`}
                  >
                    {canBulkSelect && (
                      <TableCell>
                        <Checkbox
                          data-testid={`bulk-select-${p.code}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(p.id)}
                          aria-label={`Pilih ${p.name}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-right num text-xs text-zinc-400 tabular-nums">
                      {idx + 1}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-zinc-600">
                      {codeEditId === p.id ? (
                        <Input
                          autoFocus
                          data-testid={`quick-code-input-${p.code}`}
                          value={codeEditValue}
                          onChange={(e) => setCodeEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveCodeEdit(p);
                            if (e.key === "Escape") cancelCodeEdit();
                          }}
                          onBlur={() => saveCodeEdit(p)}
                          className="h-7 text-xs font-mono px-1"
                        />
                      ) : (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            beginCodeEdit(p);
                          }}
                          data-testid={`quick-code-btn-${p.code}`}
                          title="Klik untuk ubah Part Number"
                          className="group inline-flex items-center gap-1 text-left rounded px-1 -mx-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                        >
                          {p.code}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 text-zinc-500 shrink-0" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-zinc-600 hidden" data-testid={`product-po-${p.code}`}>
                      {poEditId === p.id ? (
                        <Input
                          autoFocus
                          data-testid={`quick-po-input-${p.code}`}
                          value={poEditValue}
                          onChange={(e) => setPoEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") savePoEdit(p);
                            if (e.key === "Escape") cancelPoEdit();
                          }}
                          onBlur={() => savePoEdit(p)}
                          className="h-7 text-xs font-mono px-1"
                        />
                      ) : (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            beginPoEdit(p);
                          }}
                          data-testid={`quick-po-btn-${p.code}`}
                          title="Klik untuk ubah No. PO Pembelian"
                          className="group inline-flex items-center gap-1 text-left rounded px-1 -mx-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                        >
                          {p.po_number || <span className="text-zinc-300">—</span>}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 text-zinc-500 shrink-0" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.description ? (
                        <button
                          type="button"
                          onClick={() =>
                            setDescView({
                              name: p.name,
                              code: p.code,
                              description: p.description,
                            })
                          }
                          className="group inline-flex items-center gap-1.5 text-left font-medium hover:text-zinc-900 hover:underline underline-offset-2 cursor-zoom-in transition-colors"
                          title="Klik untuk lihat deskripsi lengkap"
                          data-testid={`product-name-${p.code}`}
                        >
                          <span>{p.name}</span>
                          <ZoomIn className="h-3 w-3 text-zinc-400 group-hover:text-zinc-700 shrink-0" />
                        </button>
                      ) : (
                        <div className="font-medium">{p.name}</div>
                      )}
                    </TableCell>
                    <TableCell data-testid={`product-brand-${p.code}`}>
                      {p.brand ? (
                        <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-700 text-[11px]">
                          {p.brand}
                        </Badge>
                      ) : (
                        <span className="text-xs text-zinc-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-zinc-600">{p.category}</TableCell>
                    <TableCell className="text-right num text-zinc-700">
                      {mtpEditId === p.id ? (
                        <Input
                          autoFocus
                          type="number"
                          min={0}
                          data-testid={`quick-mtp-input-${p.code}`}
                          value={mtpEditValue}
                          onChange={(e) => setMtpEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveMtpEdit(p);
                            if (e.key === "Escape") cancelMtpEdit();
                          }}
                          onBlur={() => saveMtpEdit(p)}
                          className="h-7 w-20 ml-auto text-right num px-1"
                        />
                      ) : isOwner ? (
                        <button
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); beginMtpEdit(p); }}
                          data-testid={`quick-mtp-btn-${p.code}`}
                          title="Klik untuk ubah Qty MTP (selisih akan pindah ke LTC)"
                          className="group inline-flex items-center gap-1 rounded px-1 -mx-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                        >
                          {p.stock_gudang ?? 0}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 text-zinc-500 shrink-0" />
                        </button>
                      ) : (
                        <>{p.stock_gudang ?? 0}</>
                      )}
                    </TableCell>
                    <TableCell className="text-right num text-zinc-700">
                      {ltcEditId === p.id ? (
                        <Input
                          autoFocus
                          type="number"
                          min={0}
                          data-testid={`quick-ltc-input-${p.code}`}
                          value={ltcEditValue}
                          onChange={(e) => setLtcEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveLtcEdit(p);
                            if (e.key === "Escape") cancelLtcEdit();
                          }}
                          onBlur={() => saveLtcEdit(p)}
                          className="h-7 w-20 ml-auto text-right num px-1"
                        />
                      ) : isOwner ? (
                        <button
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); beginLtcEdit(p); }}
                          data-testid={`quick-ltc-btn-${p.code}`}
                          title="Klik untuk ubah Qty LTC (selisih akan pindah ke MTP)"
                          className="group inline-flex items-center gap-1 rounded px-1 -mx-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                        >
                          {p.stock_toko ?? 0}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 text-zinc-500 shrink-0" />
                        </button>
                      ) : (
                        <>{p.stock_toko ?? 0}</>
                      )}
                    </TableCell>
                    <TableCell className="text-right num font-semibold">
                      <span className="text-zinc-900">{total}</span>
                    </TableCell>
                    {canViewPurchasePrice && (
                      <TableCell className="text-right num hidden md:table-cell text-zinc-600">
                        {formatNumber(p.purchase_price)}
                      </TableCell>
                    )}
                    <TableCell className="text-right num font-medium">
                      <div>{formatNumber(p.sell_price)}</div>
                    </TableCell>
                    <TableCell
                      className="text-right num font-medium text-zinc-900"
                      data-testid={`product-sell-price-ppn-${p.code}`}
                    >
                      {formatNumber(Math.round((Number(p.sell_price) || 0) * 1.11))}
                    </TableCell>
                    {isOwner && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            data-testid={`btn-edit-${p.code}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(p)}
                            className="h-8 w-8"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            data-testid={`btn-delete-${p.code}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => remove(p)}
                            className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Barang" : "Tambah Barang"}</DialogTitle>
            <DialogDescription>Isi detail barang. Harga dalam Rupiah.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <Label>Kode</Label>
              <Input data-testid="input-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </div>
            <div className="col-span-1">
              <Label>Model</Label>
              <Input
                data-testid="input-model"
                placeholder="Contoh: 111.10SP, CPG1200"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Kategori</Label>
              <Input
                data-testid="input-category"
                list="product-category-options"
                placeholder="Ketik atau pilih dari daftar…"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                disabled={!canEditCategory}
                readOnly={!canEditCategory}
                className={!canEditCategory ? "bg-zinc-50 cursor-not-allowed" : ""}
                title={!canEditCategory ? "Akses edit kategori dinonaktifkan untuk role Anda" : undefined}
              />
              <datalist id="product-category-options">
                {categoryOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {!canEditCategory && (
                <p className="text-[11px] text-zinc-400 mt-1">
                  🔒 Role Anda tidak memiliki izin untuk mengedit kategori.
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label>Brand / Merek</Label>
              <Input
                data-testid="input-brand"
                list="product-brand-options"
                placeholder="Contoh: Indomie, Sania, Bear Brand…"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
              <datalist id="product-brand-options">
                {brandOptions.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>
            <div className="col-span-2">
              <Label>Nama Vendor / Supplier</Label>
              <Input
                data-testid="input-vendor"
                placeholder="Contoh: PT Sinar Padi, CV Berkah Jaya, Toko Grosir Andi…"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Tanggal Pembelian</Label>
              <Input
                data-testid="input-purchase-date"
                type="text"
                placeholder="DD/MM/YYYY (contoh: 20/02/2026)"
                value={form.purchase_date || ""}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                Ketik manual tanggal, bulan, dan tahun pembelian barang.
              </div>
            </div>
            <div className="col-span-1">
              <Label>Nomor PO Pembelian</Label>
              <Input
                data-testid="input-po-number"
                placeholder="PO-2026-001"
                value={form.po_number || ""}
                onChange={(e) => setForm({ ...form, po_number: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Nomor Invoice Pembelian</Label>
              <Input
                data-testid="input-invoice-number"
                placeholder="INV-2026-00123"
                value={form.invoice_number || ""}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Nama Barang</Label>
              <Input data-testid="input-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Deskripsi</Label>
              <Textarea
                data-testid="input-description"
                rows={3}
                placeholder="Deskripsi lengkap barang (opsional): merek, ukuran, isi, catatan supplier, dll."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Stok Gudang</Label>
              <Input
                data-testid="input-stock-gudang"
                type="number"
                inputMode="numeric"
                value={form.stock_gudang}
                onFocus={guardStockInput}
                onChange={(e) => setForm({ ...form, stock_gudang: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Stok Toko</Label>
              <Input
                data-testid="input-stock-toko"
                type="number"
                inputMode="numeric"
                value={form.stock_toko}
                onFocus={guardStockInput}
                onChange={(e) => setForm({ ...form, stock_toko: e.target.value })}
              />
            </div>
            {isOwner && (
              <>
                <div className="col-span-1">
                  <Label>Harga Beli</Label>
                  <CurrencyInput
                    data-testid="input-purchase-price"
                    value={form.purchase_price}
                    onChange={(v) => setForm({ ...form, purchase_price: v })}
                  />
                </div>
                <div className="col-span-1">
                  <Label className="flex items-center gap-1">
                    Markup <Percent className="h-3 w-3 text-zinc-400" />
                  </Label>
                  <div className="relative">
                    <Input
                      data-testid="input-markup"
                      type="number"
                      inputMode="numeric"
                      step="1"
                      placeholder="20"
                      value={
                        Number(form.purchase_price) > 0
                          ? Math.round(computeMarkup(form.purchase_price, form.sell_price))
                          : ""
                      }
                      onChange={(e) => {
                        const buy = Number(form.purchase_price) || 0;
                        const pct = Number(e.target.value) || 0;
                        const sell = Math.round(buy * (1 + pct / 100));
                        setForm({ ...form, sell_price: sell });
                      }}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">%</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Laba per unit: {formatIDR((Number(form.sell_price) || 0) - (Number(form.purchase_price) || 0))}
                  </div>
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label>Harga Jual</Label>
              <CurrencyInput
                data-testid="input-sell-price"
                value={form.sell_price}
                onChange={(v) => setForm({ ...form, sell_price: v })}
              />
            </div>
            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button data-testid="btn-save-product" type="submit" className="bg-zinc-900 hover:bg-zinc-800">
                Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* BULK EDIT DIALOG */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Massal ({selectedIds.size} barang)</DialogTitle>
            <DialogDescription>
              Kosongkan kolom yang tidak ingin diubah. Perubahan diterapkan ke semua barang terpilih.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={runBulkEdit} className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <Label>Ubah Kategori</Label>
              <Input
                data-testid="bulk-input-category"
                placeholder={canEditCategory ? "Kosong = tidak diubah" : "Terkunci untuk role ini"}
                value={bulkForm.category}
                onChange={(e) => setBulkForm({ ...bulkForm, category: e.target.value })}
                disabled={!canEditCategory}
                readOnly={!canEditCategory}
                className={!canEditCategory ? "bg-zinc-50 cursor-not-allowed" : ""}
              />
            </div>
            <div className="col-span-1">
              <Label>Ubah Brand</Label>
              <Input
                data-testid="bulk-input-brand"
                placeholder="Kosong = tidak diubah"
                value={bulkForm.brand}
                onChange={(e) => setBulkForm({ ...bulkForm, brand: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Set Markup (%)</Label>
              <Input
                data-testid="bulk-input-markup"
                type="number"
                inputMode="numeric"
                placeholder="mis. 25 → harga jual = harga beli × 1.25"
                value={bulkForm.markup_percent}
                onChange={(e) => setBulkForm({ ...bulkForm, markup_percent: e.target.value })}
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                Kosong = harga jual tidak diubah. Butuh Harga Beli &gt; 0.
              </div>
            </div>
            <div className="col-span-1">
              <Label>Tambah Stok Gudang</Label>
              <Input
                data-testid="bulk-input-adjust-gudang"
                type="number"
                inputMode="numeric"
                placeholder="+10 / -3"
                value={bulkForm.adjust_gudang}
                onChange={(e) => setBulkForm({ ...bulkForm, adjust_gudang: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Tambah Stok Toko</Label>
              <Input
                data-testid="bulk-input-adjust-toko"
                type="number"
                inputMode="numeric"
                placeholder="+10 / -3"
                value={bulkForm.adjust_toko}
                onChange={(e) => setBulkForm({ ...bulkForm, adjust_toko: e.target.value })}
              />
            </div>
            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setBulkOpen(false)}>Batal</Button>
              <Button data-testid="btn-bulk-save" type="submit" className="bg-zinc-900 hover:bg-zinc-800">
                Terapkan ke {selectedIds.size} barang
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Description Zoom Dialog */}
      <Dialog open={!!descView} onOpenChange={(o) => !o && setDescView(null)}>
        <DialogContent className="max-w-2xl" data-testid="desc-zoom-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ZoomIn className="h-4 w-4" />
              Deskripsi Lengkap
            </DialogTitle>
            {descView && (
              <DialogDescription>
                <span className="font-medium text-zinc-800">{descView.name}</span>
                <span className="mx-1.5 text-zinc-400">·</span>
                <span className="font-mono text-[11px] text-zinc-500">{descView.code}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          {descView && (
            <div
              className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap break-words border border-zinc-200 rounded-md bg-zinc-50 p-4 max-h-[60vh] overflow-y-auto"
              data-testid="desc-zoom-content"
            >
              {descView.description}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDescView(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
